/* Sweet alert — kept for the prompt dialogs only (input, preConfirm) */
import Swal from "sweetalert2"

/* Components */
import { pushToast } from "../components/Toast"
import { pushConfirm } from "../components/Confirm"


/* SWEETALERT ICON — TOAST TYPE */
const TYPES = {
    success: "success",
    error: "error",
    warning: "warning",
    info: "info",
    question: "info",
}

/* CONFIRM BUTTON COLORS THAT MEAN A DESTRUCTIVE ACTION */
const DANGER = ["#f44336", "#e53935", "#d33", "#dc3545", "#ef5350", "#f75454", "red"]

/* THE ANSWER SHAPE OF SWEETALERT — CALL SITES READ result.isConfirmed */
const answer = (confirmed) => ({
    isConfirmed: confirmed,
    isDenied: false,
    isDismissed: !confirmed,
    value: confirmed ? true : undefined,
    dismiss: confirmed ? undefined : Swal.DismissReason.cancel,
})


/* STRIP THE MARKUP THE OLD NOTIFICATION WRAPPED THE TEXT WITH */
const getText = (options) => {

    /* Plain Message */
    if (options.message) {
        return options.message
    }

    /* Html Message */
    if (options.html) {
        return String(options.html).replace(/<p class="notification">/g, "").replace(/<\/p>/g, "")
    }

    /* Title And Text */
    return [options.title, options.text].filter((item) => item).join("<br />")
}


/* A DESTRUCTIVE CONFIRMATION IS DRAWN IN RED */
const isDanger = (options) => {

    /* Check Color */
    if (options.confirmButtonColor) {
        return DANGER.includes(String(options.confirmButtonColor).toLowerCase())
    }

    /* Check Icon */
    return options.icon === "warning" || options.icon === "error"
}


/* A PROMPT STAYS ON SWEETALERT — THE CUSTOM COMPONENTS DO NOT HANDLE INPUTS */
const isPrompt = (options) => Boolean(options.input || options.preConfirm || options.didOpen || options.willOpen || options.customClass?.input)


/*
    SWEETALERT COMPATIBLE FACADE

    Toast — every utils.notification() call
    Confirm — every dialog with a cancel button
    Sweetalert — the prompt dialogs only (input, preConfirm)
*/
const alert = {

    fire: (options, ...rest) => {

        /* Shorthand Call — Swal.fire("Title", "Text") */
        if (typeof options === "string") {
            pushToast({ text: [options, rest[0]].filter((item) => typeof item === "string").join("<br />"), type: TYPES[rest[1]] ? TYPES[rest[1]] : "info" })
            return Promise.resolve(answer(true))
        }

        /* Check Options */
        if (!options || typeof options !== "object") {
            return Promise.resolve(answer(true))
        }

        /* Notification — goes to the bottom toast */
        if (options.notification) {
            pushToast({ text: getText(options), type: TYPES[options.icon] ? TYPES[options.icon] : "info", timer: options.timer ? options.timer : 2600 })
            return Promise.resolve(answer(true))
        }

        /* Prompt — sweetalert draws it */
        if (isPrompt(options)) {
            return Swal.fire(options, ...rest)
        }

        /* Confirmation — goes to the custom modal */
        if (options.showCancelButton) {
            return pushConfirm({
                title: options.title,
                text: options.text,
                icon: options.icon,
                danger: isDanger(options),
                confirmText: options.confirmButtonText,
                cancelText: options.cancelButtonText,
            }).then((confirmed) => answer(confirmed))
        }

        /* Anything Else — a plain message, so a toast is enough */
        pushToast({ text: getText(options), type: TYPES[options.icon] ? TYPES[options.icon] : "info", timer: options.timer ? options.timer : 2600 })
        return Promise.resolve(answer(true))
    },

    /* Passed Through — used by the prompt dialogs */
    showValidationMessage: (...args) => Swal.showValidationMessage(...args),
    resetValidationMessage: (...args) => Swal.resetValidationMessage(...args),
    getPopup: (...args) => Swal.getPopup(...args),
    getInput: (...args) => Swal.getInput(...args),
    showLoading: (...args) => Swal.showLoading(...args),
    hideLoading: (...args) => Swal.hideLoading(...args),
    isVisible: (...args) => Swal.isVisible(...args),
    update: (...args) => Swal.update(...args),
    close: (...args) => Swal.close(...args),
    DismissReason: Swal.DismissReason,
}


export default alert
